import React, { useState } from "react";
import { Mail, Phone, MapPin, Send } from "lucide-react";

export default function ContactUs() {
  const [loading, setLoading] = useState(false);

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
  });

  const handleChange = (e) => {
    setFormData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    setLoading(true);

    try {
      const res = await fetch("https://webxsecure.in/api/contact.php", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
      });

      const data = await res.json();

      if (data.success) {
        alert("Message Sent Successfully");

        setFormData({
          name: "",
          email: "",
          phone: "",
          message: "",
        });
      } else {
        alert(data.message || "Failed to send message");
      }
    } catch (err) {
      console.log("Server Error");
      alert("Server Error");
    } finally {
      setLoading(false);
    }
  };

  const contactItems = [
    {
      icon: Mail,
      title: "Email Us",
      val: "contact@webxsecure.in",
    },
    {
      icon: Phone,
      title: "Call Us",
      val: "+91 9785549138",
    },
    {
      icon: MapPin,
      title: "Our Office",
      val: "Bhagat Ki Kothi, Jodhpur, Rajasthan 342001",
    },
  ];

  return (
    <section className="relative overflow-hidden bg-[#fbfbfb] px-6 pb-16 dark:bg-[#050505] lg:px-16 md:py-16">
      {/* Glow Effects */}
      <div className="pointer-events-none absolute left-0 top-0 h-[500px] w-[500px] rounded-full bg-primary/10 blur-[140px]" />
      <div className="pointer-events-none absolute bottom-0 right-0 h-[500px] w-[500px] rounded-full bg-blue-500/5 blur-[140px]" />

      {/* Grid */}
      <div className="absolute inset-0 bg-[linear-gradient(rgba(0,0,0,1)_1px,transparent_1px),linear-gradient(90deg,rgba(0,0,0,1)_1px,transparent_1px)] bg-[size:60px_60px] opacity-[0.03] dark:bg-[linear-gradient(rgba(255,255,255,1)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,1)_1px,transparent_1px)] dark:opacity-[0.05]" />

      <div className="relative z-10 mx-auto grid max-w-7xl gap-16 lg:grid-cols-2">
        {/* Form */}
        <div className="rounded-lg border border-gray-200 bg-white p-8 dark:border-gray-800 dark:bg-[#0f0f0f] md:p-10">
          <h3 className="mb-2 text-2xl font-bold text-gray-900 dark:text-white">
            Send a Message
          </h3>

          <p className="mb-8 text-gray-500 dark:text-gray-400">
            Have a project in mind? We'd love to hear from you.
          </p>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="grid gap-5 md:grid-cols-2">
              <input
                type="text"
                name="name"
                required
                value={formData.name}
                onChange={handleChange}
                placeholder="Full Name"
                className="w-full rounded-lg border border-gray-200 bg-gray-50 p-4 text-gray-900 outline-none transition-all focus:ring-1 focus:ring-primary dark:border-gray-800 dark:bg-[#161616] dark:text-white"
              />

              <input
                type="email"
                name="email"
                required
                value={formData.email}
                onChange={handleChange}
                placeholder="Email Address"
                className="w-full rounded-lg border border-gray-200 bg-gray-50 p-4 text-gray-900 outline-none transition-all focus:ring-1 focus:ring-primary dark:border-gray-800 dark:bg-[#161616] dark:text-white"
              />
            </div>

            <input
              type="tel"
              name="phone"
              required
              value={formData.phone}
              onChange={handleChange}
              placeholder="Phone Number"
              className="w-full rounded-lg border border-gray-200 bg-gray-50 p-4 text-gray-900 outline-none transition-all focus:ring-1 focus:ring-primary dark:border-gray-800 dark:bg-[#161616] dark:text-white"
            />

            <textarea
              rows="5"
              name="message"
              required
              value={formData.message}
              onChange={handleChange}
              placeholder="Tell us about your project..."
              className="w-full rounded-lg border border-gray-200 bg-gray-50 p-4 text-gray-900 outline-none transition-all focus:ring-1 focus:ring-primary dark:border-gray-800 dark:bg-[#161616] dark:text-white"
            />

            <button
              type="submit"
              disabled={loading}
              className="flex w-full items-center justify-center gap-2 rounded-lg bg-primary py-4 font-bold text-white transition-all hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-70"
            >
              {loading ? "Sending..." : "Send Request"}
              <Send size={18} />
            </button>
          </form>
        </div>

        {/* Contact Info */}
        <div className="space-y-10 lg:pt-10">
          <div>
            <h4 className="mb-3 text-sm font-bold uppercase tracking-widest text-orange-500">
              Contact Information
            </h4>

            <h2 className="mb-6 text-4xl font-black text-gray-900 dark:text-white md:text-5xl">
              Let's
              <span className="bg-gradient-to-r from-primary via-orange-400 to-yellow-500 bg-clip-text text-transparent">
                {" "}
                Connect
              </span>
            </h2>

            <p className="text-lg leading-relaxed text-gray-600 dark:text-gray-400">
              Partner with Web X Secure to accelerate your digital growth. Our
              team is ready to discuss your project requirements and create a
              strategy tailored to your business goals.
            </p>
          </div>

          <div className="space-y-8">
            {contactItems.map((item, index) => (
              <div key={index} className="flex items-start gap-4">
                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-primary/10 text-primary">
                  <item.icon size={24} />
                </div>

                <div>
                  <h5 className="text-lg font-bold text-gray-900 dark:text-white">
                    {item.title}
                  </h5>

                  <p className="text-gray-500 dark:text-gray-400">{item.val}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
